// src/pages/NotesPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
    Box, Typography, IconButton, Avatar, Tooltip, Select, MenuItem, FormControl,
} from '@mui/material';
import {
    Add, Search, LightMode, DarkMode, ArrowBack,
} from '@mui/icons-material';
import { useNotes } from '../context/NotesContext';
import NoteCard from '../components/notes/NoteCard';
import NotesPanel from '../components/notes/NotesPanel';
import useNoteSearch from '../hooks/useNoteSearch';
import '../styles/notes.css';

const SORT_OPTIONS = [
    { value: 'updatedAt', label: 'Last Modified' },
    { value: 'createdAt', label: 'Date Created' },
    { value: 'title', label: 'Title A–Z' },
];

const NotesPage = () => {
    const {
        isDarkMode, toggleTheme, getUserInitials, getDisplayName,
        handleMenuOpen,
    } = useOutletContext();

    const navigate = useNavigate();
    const {
        notes, pagination, isLoading, error, fetchNotes,
        openNotePanel, closeNotePanel, isPanelOpen, panelNoteId, panelInitialContent,
    } = useNotes();

    const { query, setQuery, results, isSearching, clearSearch } = useNoteSearch();

    const [sortBy, setSortBy] = useState('updatedAt');
    const [filterCompany, setFilterCompany] = useState('');

    // Fetch on mount and when sort/filter changes
    useEffect(() => {
        fetchNotes({
            sortBy,
            sortOrder: sortBy === 'title' ? 'asc' : 'desc',
            companyId: filterCompany || undefined,
        });
    }, [sortBy, filterCompany, fetchNotes]);

    // Decide which list to show: search results or full list
    const displayNotes = query.trim().length >= 2 ? results : notes;

    const handleNewNote = () => openNotePanel(null, null);

    const handleCardClick = (noteId) => openNotePanel(noteId);

    const handleLoadMore = useCallback(() => {
        if (!pagination.hasMore || isLoading) return;
        fetchNotes({
            page: pagination.page + 1,
            sortBy,
            sortOrder: sortBy === 'title' ? 'asc' : 'desc',
            companyId: filterCompany || undefined,
        });
    }, [pagination, isLoading, sortBy, filterCompany, fetchNotes]);

    // Collect unique companies from current notes for filter dropdown
    const uniqueCompanies = [...new Set(notes.flatMap(n => n.companyIds || []))].sort();

    return (
        <Box className="notes-page">
            {/* ── Top bar ─────────────────────────────────────────── */}
            <Box className="notes-page-topbar">
                <Box className="notes-page-topbar-left">
                    <Tooltip title="Home">
                        <IconButton onClick={() => navigate('/home')} aria-label="Home" sx={{ color: 'var(--text-2)' }}>
                            <ArrowBack fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            color: 'var(--text-1)',
                            cursor: 'pointer',
                            letterSpacing: '-0.02em',
                        }}
                        onClick={() => navigate('/home')}
                    >
                        Finlytics<span style={{ color: 'var(--accent)' }}>.</span>
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{ color: 'var(--text-2)', fontWeight: 600, ml: 2, borderLeft: '1px solid var(--border)', pl: 2 }}
                    >
                        Research Notes
                    </Typography>
                </Box>

                <Box className="notes-page-topbar-right">
                    <Tooltip title={isDarkMode ? 'Light mode' : 'Dark mode'}>
                        <IconButton onClick={toggleTheme} sx={{ color: 'var(--text-2)' }}>
                            {isDarkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={getDisplayName()}>
                        <IconButton onClick={handleMenuOpen} size="small">
                            <Avatar sx={{
                                width: 32, height: 32,
                                bgcolor: 'var(--accent)',
                                fontSize: '0.85rem', fontWeight: 700,
                            }}>
                                {getUserInitials()}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* ── Content area ────────────────────────────────────── */}
            <Box className="notes-page-content">
                {/* Toolbar row */}
                <Box className="notes-page-toolbar">
                    {/* Search */}
                    <div className="notes-search-wrapper">
                        <Search className="notes-search-icon" />
                        <input
                            className="notes-search-input"
                            type="text"
                            placeholder="Search notes..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {query && (
                            <button className="notes-search-clear" onClick={clearSearch}>&times;</button>
                        )}
                    </div>

                    {/* Filters / Sort */}
                    <div className="notes-toolbar-actions">
                        {uniqueCompanies.length > 0 && (
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select
                                    value={filterCompany}
                                    onChange={(e) => setFilterCompany(e.target.value)}
                                    displayEmpty
                                    sx={{
                                        bgcolor: 'var(--input-bg)',
                                        color: 'var(--text-1)',
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
                                        fontSize: '0.85rem',
                                        height: 36,
                                    }}
                                >
                                    <MenuItem value="">All Companies</MenuItem>
                                    {uniqueCompanies.map((c) => (
                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                sx={{
                                    bgcolor: 'var(--input-bg)',
                                    color: 'var(--text-1)',
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border)' },
                                    fontSize: '0.85rem',
                                    height: 36,
                                }}
                            >
                                {SORT_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <button className="notes-new-btn" onClick={handleNewNote}>
                            <Add sx={{ fontSize: 18, mr: 0.5 }} /> New Note
                        </button>
                    </div>
                </Box>

                {/* Stats row */}
                <div className="notes-page-stats">
                    <span className="notes-page-stat">
                        {isSearching ? 'Searching...' : `${displayNotes.length} note${displayNotes.length !== 1 ? 's' : ''}`}
                    </span>
                    {query && results.length > 0 && (
                        <span className="notes-page-stat notes-page-stat--muted">
                            matching "{query}"
                        </span>
                    )}
                </div>

                {/* Notes grid */}
                {error && (
                    <div className="notes-page-error">
                        <p>Failed to load notes: {error}</p>
                        <button onClick={() => fetchNotes()}>Retry</button>
                    </div>
                )}

                {!error && displayNotes.length === 0 && !isLoading && (
                    <div className="notes-page-empty">
                        <div className="notes-page-empty-icon">📝</div>
                        <h3>No research notes yet</h3>
                        <p>Start capturing your financial insights and analysis.</p>
                        <button className="notes-new-btn" onClick={handleNewNote}>
                            <Add sx={{ fontSize: 18, mr: 0.5 }} /> Create Your First Note
                        </button>
                    </div>
                )}

                <div className="notes-card-grid">
                    {displayNotes.map((note) => (
                        <NoteCard
                            key={note._id || note.noteId}
                            note={note}
                            onClick={() => handleCardClick(note._id || note.noteId)}
                        />
                    ))}
                </div>

                {pagination.hasMore && !query && (
                    <div className="notes-page-load-more">
                        <button
                            className="notes-load-more-btn"
                            onClick={handleLoadMore}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Loading...' : 'Load More'}
                        </button>
                    </div>
                )}

                {isLoading && displayNotes.length === 0 && (
                    <div className="notes-page-loading">
                        <div className="notes-spinner" />
                        <span>Loading notes...</span>
                    </div>
                )}
            </Box>

            {/* Editor Panel (right slide-over) */}
            <NotesPanel
                isOpen={isPanelOpen}
                onClose={closeNotePanel}
                noteId={panelNoteId}
                initialContent={panelInitialContent}
            />
        </Box>
    );
};

export default NotesPage;
